import { AxiosInstance } from 'axios';

/**
 * The connection between a customer environment and Exact Online.
 *
 * This client is kept apart from api.ts because that file is generated by nswag and gets
 * overwritten on the next generation. The axios instance was created with
 * transformResponse: [], so response.data is a string and has to be parsed here.
 */

export type ExactConnectionState =
    | 'Unknown'
    | 'NotConfigured'
    | 'NoToken'
    | 'Connected'
    | 'Refreshing'
    | 'Expired';

export interface ExactConnection {
    customer: string;
    environment: string;
    state: ExactConnectionState;
    endpoint: string;
    division?: number | null;
    clientId?: string | null;
    clientSecretConfigured: boolean;
    /** Where Exact sends the browser back to after signing in. */
    redirectUri?: string | null;
    redirectUriWarning?: string | null;
    /** The URL that starts the Exact login. Null when there is nothing to open. */
    authorizeUrl?: string | null;
    tokenFound: boolean;
    expiresAt?: string | null;
    /** Negative as soon as the access token has expired. */
    expiresInSeconds?: number | null;
    /** When the connection was last refreshed by hand. */
    reconnectedAt?: string | null;
    hasRefreshToken: boolean;
    /** Whether the last refresh actually replaced the token. */
    refreshed?: boolean | null;
    summary: string;
    error?: string | null;
}

/**
 * How much of the daily Exact API budget this environment has left. Mirrors
 * ExactRateLimitDto.
 *
 * Every field is nullable because the reading is a log line rather than a measurement:
 * Exact sends three headers back on every call, the connector writes them down, and this is
 * the newest of those lines. So the numbers are as old as `measuredAt`, and an environment
 * that has not called Exact today has `found: false` rather than a zero.
 */
export interface ExactRateLimit {
    customer: string;
    environment: string;
    found: boolean;
    /** Calls allowed per 24-hour window. */
    limit?: number | null;
    /** Calls left when the line was written. */
    remaining?: number | null;
    /** Calls used by then; null when either half is missing. */
    used?: number | null;
    /** When the window rolls over and the count starts again. */
    resetAt?: string | null;
    /** When the reading was written, which is when Exact was last called. */
    measuredAt?: string | null;
    /** The call it came off, as the connector logged it. */
    call?: string | null;
    /** The raw log line, so a reading that parses oddly can still be read. */
    line?: string | null;
    daysSearched: number;
    error?: string | null;
}

const parse = <T,>(data: any): T =>
    typeof data === 'string' ? (JSON.parse(data) as T) : (data as T);

export const getExactConnection = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<ExactConnection> => {
    const response = await axios.get(
        `/Exact/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/connection`
    );

    return parse<ExactConnection>(response.data);
};

/**
 * Replaces the access token using the refresh token that is already there, exactly like
 * the timer does when the token is about to expire. Does nothing while the current token is still valid.
 */
export const refreshExactConnection = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<ExactConnection> => {
    const response = await axios.post(
        `/Exact/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/connection/refresh`
    );

    return parse<ExactConnection>(response.data);
};

/**
 * The newest rate limit reading out of the log of this environment.
 *
 * Apart from the connection call on purpose: this one searches a log partition and the
 * other reads a handful of settings, so folding them together would make the panel wait for
 * a table scan on every refresh.
 */
export const getExactRateLimit = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<ExactRateLimit> => {
    const response = await axios.get(
        `/Exact/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/ratelimit`
    );

    return parse<ExactRateLimit>(response.data);
};
